import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './Marketplace.css'; // This should contain the styles for this component
import SellerRating from './SellerRating';

// Add this utility function near the top of your component (after imports)
const formatPaymentMethods = (paymentMethods) => {
  try {
    // Handle different formats of payment methods
    let methodsObj;
    
    if (!paymentMethods) {
      return 'Keine angegeben';
    }
    
    // If it's already a JS object
    if (typeof paymentMethods === 'object' && !Array.isArray(paymentMethods)) {
      methodsObj = paymentMethods;
    } 
    // If it's a JSON string (possibly escaped)
    else if (typeof paymentMethods === 'string') {
      // Handle escaped JSON string (replace \" with ")
      const cleanJson = paymentMethods.replace(/\\"/g, '"');
      methodsObj = JSON.parse(cleanJson);
    } 
    else {
      return 'Keine angegeben';
    }
    
    // Map payment methods to readable labels and filter enabled ones
    const methodLabels = {
      cash: 'Barzahlung',
      bank_transfer: 'Überweisung',
      paypal: 'PayPal'
    };
    
    const enabledMethods = Object.entries(methodsObj)
      .filter(([_, enabled]) => enabled === true)
      .map(([method, _]) => methodLabels[method] || method);
    
    return enabledMethods.length > 0 ? enabledMethods.join(', ') : 'Keine angegeben';
  } catch (error) {
    console.error('Error parsing payment methods:', error, paymentMethods);
    return 'Fehler beim Anzeigen der Zahlungsmethoden';
  }
};

// Add this helper function near the top of your component, before the return statement
const isNegotiable = (listing) => {
  if (!listing) return false;
  
  // Check different possible formats
  return listing.is_negotiable === 'Y' || 
         listing.is_negotiable === true || 
         listing.is_negotiable === 'true' || 
         listing.is_negotiable === 1 || 
         listing.is_negotiable === '1';
}

// Add this helper function near the other utility functions at the top

const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'gerade eben';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `vor ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `vor ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    if (days < 7) {
      return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
    } else if (days < 30) {
      const weeks = Math.floor(days / 7);
      return `vor ${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`;
    } else {
      return date.toLocaleDateString('de-DE');
    }
  }
};

// Replace the existing SellerTrustBadge component

const SellerTrustBadge = ({ trustScore, ratingCount }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isTrusted = trustScore >= 25;
  
  return (
    <div className="seller-trust-badge">
      <div className={`trust-badge ${isTrusted ? 'trusted' : 'unverified'}`}>
        <span className="material-symbols-outlined">
          {isTrusted ? 'verified' : 'gpp_maybe'}
        </span>
        <span className="trust-label">
          {isTrusted ? 'TRUSTED-SELLER' : 'SELLER-UNVERIFIED'}
        </span>
      </div>
      
      <div className="trust-info-container">
        <span 
          className="material-symbols-outlined info-icon" 
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          info
        </span>
        
        {showTooltip && (
          <div className="trust-tooltip-wrapper">
            <div className="rating-tooltip">
              <h4>Verkäufer-Bewertungssystem</h4>
              <p>Unser Bewertungssystem basiert auf Trust-Score:</p>
              <ul>
                <li><span className="rating-top">TOP</span>: +5 Punkte</li>
                <li><span className="rating-ok">OK</span>: +2 Punkte</li>
                <li><span className="rating-negative">NEGATIV</span>: -2 Punkte</li>
              </ul>
              <p>Ab 25 Punkten wird ein Verkäufer als <strong>TRUSTED</strong> eingestuft, was auf mindestens 3 positive Verkäufe hinweist.</p>
              <p>Aktueller Score: <strong>{trustScore}</strong></p>
              <p>Bewertungen: <strong>{ratingCount}</strong></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MarketplaceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [contactExpanded, setContactExpanded] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImageIndex, setPopupImageIndex] = useState(0);
  const [userId, setUserId] = useState(() => {
    try {
      // Get user ID from user object first (preferred method)
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData && userData.id) {
          return userData.id.toString();
        }
      }
      
      // Fallback to direct userId if available
      const directId = localStorage.getItem('userId');
      return directId ? directId.toString() : null;
    } catch (e) {
      console.error('Error parsing userId from localStorage:', e);
      return null;
    }
  });
  const [deactivating, setDeactivating] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratings, setRatings] = useState([]);

  // Helper function to validate token and extract user info
  const validateAuthToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return token;
  };
  
  // Helper function to safely compare user IDs regardless of type
  const compareUserIds = (id1, id2) => {
    try {
      // Handle case where id1 might be an object
      let userId1 = id1;
      if (typeof id1 === 'object' && id1 !== null) {
        userId1 = id1.id; // Extract ID from object
      }
      
      // Make sure we have values to compare
      if (!userId1 || !id2) return false;
      
      // Compare as integers (convert both to strings first for safety)
      return parseInt(String(userId1)) === parseInt(String(id2));
    } catch (err) {
      console.error('Error comparing user IDs:', err);
      return false;
    }
  };

  useEffect(() => {
    // Combined authentication check
    const checkAuth = () => {
      const token = validateAuthToken();
      setIsAuthenticated(!!token);
      
      try {
        const storedUserId = localStorage.getItem('userId');
        const userObj = JSON.parse(localStorage.getItem('user') || '{}');
        
        // First try userId directly (string format), then try from user object
        if (storedUserId) {
          setUserId(String(storedUserId));
        } else if (userObj && userObj.id) {
          setUserId(String(userObj.id));
        } else {
          setUserId(null);
        }
        
      } catch (e) {
        console.error('Error in checkAuth:', e);
      }
    };
    
    checkAuth();
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  useEffect(() => {
    const fetchListing = async () => {
      setLoading(true);
      try {
        // Get token if available for auth-only listings
        const token = localStorage.getItem('token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`/api/marketplace/${id}`, { headers });
        
        if (response.status === 403) {
          const data = await response.json();
          setError('Dieses Angebot ist nur für registrierte Benutzer sichtbar.');
          setLoading(false);
          return;
        }
        
        if (!response.ok) {
          throw new Error('Angebot konnte nicht geladen werden');
        }
        
        const data = await response.json();
        
        // Convert image data to correct format if needed
        if (data.images) {
          // Handle both string array and object array formats
          if (typeof data.images[0] === 'string') {
            // Convert string paths to objects with image_path property
            data.images = data.images.map(path => ({ image_path: path }));
          } else if (data.images[0] && !data.images[0].image_path && data.images[0].path) {
            // Handle {path: '...'} format
            data.images = data.images.map(img => ({ image_path: img.path }));
          }
        } else {
          data.images = [];
        }
        
        setListing(data);
      } catch (error) {
        console.error('Error fetching listing:', error);
        setError('Fehler beim Laden des Angebots');
      } finally {
        setLoading(false);
      }

      // Fetch user email if authenticated
      if (isAuthenticated) {
        try {
          const userResponse = await fetch('/api/user/profile', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUserEmail(userData.email || '');
            
            // Pre-fill contact form with user data
            setContactForm(prev => ({
              ...prev,
              name: userData.username || '',
              email: userData.email || ''
            }));
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    
    fetchListing();
    fetchRatings();
  }, [id, isAuthenticated]);

  const fetchRatings = async () => {
    try {
      const response = await fetch(`/api/marketplace/${id}/ratings`);
      
      if (response.ok) {
        const data = await response.json();
        setRatings(data);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const handleDeleteListing = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Sie müssen angemeldet sein, um ein Angebot zu löschen.');
        return;
      }
      
      const response = await fetch(`/api/marketplace/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Löschen des Angebots');
      }
      
      navigate('/marketplace');
    } catch (error) {
      console.error('Error deleting listing:', error);
      setError(error.message);
      setShowConfirmDelete(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Captcha-Validierung entfernt
      
      // Nachricht senden
      const response = await fetch('/api/marketplace/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          listingId: id,  // Make sure parameter names match what the backend expects
          name: contactForm.name,
          email: userEmail,
          message: contactForm.message
        })
      });
      
      if (response.ok) {
        setFormMessage({
          type: 'success',
          text: 'Ihre Nachricht wurde erfolgreich gesendet!'
        });
        
        // Optional: Formular zurücksetzen oder schließen nach erfolgreicher Sendung
        setContactForm({
          name: contactForm.name,
          email: contactForm.email,
          message: ''
        });
        
        // Optional: Nach erfolgreichem Senden Formular automatisch schließen
        setTimeout(() => {
          setShowContactForm(false);
          setFormMessage(null);
        }, 3000);
      } else {
        const error = await response.json();
        setFormMessage({
          type: 'error',
          text: error.message || 'Beim Senden der Nachricht ist ein Fehler aufgetreten.'
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setFormMessage({
        type: 'error',
        text: 'Ein Netzwerkfehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setDeactivating(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Sie müssen angemeldet sein, um ein Angebot zu deaktivieren');
      }
      
      const response = await fetch(`/api/marketplace/${id}/deactivate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Deaktivieren des Angebots');
      }
      
      // Update listing status in state
      setListing(prev => ({
        ...prev,
        is_active: 'N'
      }));
      
      setMessage('Angebot erfolgreich deaktiviert');
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deactivating listing:', error);
      setError(error.message);
    } finally {
      setDeactivating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="marketplace-detail-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Lade Angebotsdetails...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="marketplace-detail-container">
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (requiresLogin) {
    return (
      <div className="marketplace-detail-container">
        <div className="login-required-message">
          <p>Dieses Angebot ist nur für registrierte Benutzer sichtbar.</p>
          <Link to="/login" className="btn btn-primary">Anmelden</Link>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="marketplace-detail-container">
        <div className="not-found-message">
          <span className="material-symbols-outlined">search_off</span>
          <p>Angebot nicht gefunden</p>
          <Link to="/marketplace" className="back-button">
            Zurück zum Gebrauchtmarkt
          </Link>
        </div>
      </div>
    );
  }

  const showEditOptions = listing && listing.is_owner;

  return (
    <div className="marketplace-detail-container">
      <div className="detail-navigation">
        <Link to="/marketplace" className="back-button">
          <span className="material-symbols-outlined">arrow_back</span>
          Zurück zum Gebrauchtmarkt
        </Link>
        
        {isAuthenticated && listing && listing.is_owner && (
          <div className="owner-actions">
            <Link to={`/marketplace/edit/${id}`} className="edit-button">
              <span className="material-symbols-outlined">edit</span>
              Bearbeiten
            </Link>
            <button onClick={() => setShowConfirmDelete(true)} className="delete-button">
              <span className="material-symbols-outlined">delete</span>
              Löschen
            </button>
            {listing.is_active ? (
              <button onClick={handleDeactivate} className="deactivate-button">
                <span className="material-symbols-outlined">visibility_off</span>
                Deaktivieren
              </button>
            ) : (
              <button onClick={handleReactivate} className="reactivate-button">
                <span className="material-symbols-outlined">visibility</span>
                Reaktivieren
              </button>
            )}
          </div>
        )}

        {!isAuthenticated && (
          <div className="login-prompt">
            <p>Sie müssen <Link to="/login">eingeloggt</Link> sein, um Ihre Angebote zu verwalten.</p>
          </div>
        )}
      </div>
      
      <div className="listing-detail-content">
        <div className="listing-gallery">
          <div className="main-image-container">
            {listing.images && listing.images.length > 0 ? (
              <img 
                src={listing.images[selectedImage]?.image_path} 
                alt={listing.title} 
                className="main-image"
                onClick={() => {
                  setPopupImageIndex(selectedImage);
                  setShowImagePopup(true);
                }}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <div className="no-image">
                <span className="material-symbols-outlined">image_not_supported</span>
                <p>Keine Bilder verfügbar</p>
              </div>
            )}
          </div>
          
          {listing.images && listing.images.length > 1 && (
            <div className="thumbnail-gallery">
              {listing.images.map((image, index) => (
                <div 
                  key={index} 
                  className={`thumbnail ${selectedImage === index ? 'selected' : ''}`}
                  onClick={() => setSelectedImage(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <img 
                    src={image?.image_path} 
                    alt={`Thumbnail ${index + 1}`} 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/placeholder-image.png'; // Add a placeholder image path
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="listing-info">
          <h1>{listing.title}</h1>
          
          {/* Bereich mit Preis und FP/VB Badge */}
          <div className="listing-detail-section">
            <div className="listing-detail-price-container">
              <h2 className="listing-price">
                {parseFloat(listing.price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </h2>
              
              {/* Prominenter Badge für Festpreis oder Verhandelspreis */}
              <div className={`price-type-badge ${listing.is_negotiable === 'Y' ? 'vb-badge' : 'fp-badge'}`}>
                {listing.is_negotiable === 'Y' ? 'VB' : 'FP'}
                <span className="price-type-label">
                  {listing.is_negotiable === 'Y' ? 'Verhandelbar' : 'Festpreis'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="listing-meta">
            <div className="meta-item">
              <span className="material-symbols-outlined">person</span>
              <span>Anbieter: {listing.username}</span>
            </div>
            
            {/* Add this category meta-item */}
            {listing.category_name && (
              <div className="meta-item">
                <span className="material-symbols-outlined">category</span>
                <span>Kategorie: {listing.category_name}</span>
              </div>
            )}
            
            <div className="meta-item">
              <span className="material-symbols-outlined">location_on</span>
              <span>Standort: {listing.city}, {listing.zip} ({listing.country_code})</span>
            </div>
            
            <div className="meta-item">
              <span className="material-symbols-outlined">
                {listing.shipping_available === 'Y' && listing.pickup_preferred === 'Y' 
                  ? 'local_shipping' 
                  : listing.shipping_available === 'Y' 
                    ? 'local_shipping' 
                    : 'store'}
              </span>
              <span>
                {listing.shipping_available === 'Y' && listing.pickup_preferred === 'Y'
                  ? 'Versand möglich & Abholung bevorzugt'
                  : listing.shipping_available === 'Y'
                    ? 'Versand möglich'
                    : 'Nur Abholung'}
              </span>
            </div>

            <div className="meta-item">
              <span className="material-symbols-outlined">
                payments
              </span>
              <span>
                Zahlungsmethoden: {formatPaymentMethods(listing.payment_methods)}
              </span>
            </div>
            
            {listing.weight && (
              <div className="meta-item">
                <span className="material-symbols-outlined">
                  scale
                </span>
                <span>Gewicht: {listing.weight} kg</span>
              </div>
            )}
            
            <div className="meta-item">
              <span className="material-symbols-outlined">calendar_today</span>
              <span>Eingestellt am: {new Date(listing.created_at).toLocaleDateString('de-DE')}</span>
            </div>

            <div className="meta-item purchase-agreement">
              <span className="material-symbols-outlined">description</span>
              <a 
                href={`/api/marketplace/${id}/purchase-agreement`} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="download-contract-link"
              >
                Kaufvertrag-Vordruck herunterladen
              </a>
            </div>
          </div>
          
          <div className="listing-description">
            <h3>Beschreibung</h3>
            <p>{listing.description}</p>
          </div>
          
        </div>
        
        <div className="listing-details-section">
          {/* Existing listing details */}
          
          {/* Add the disclaimer banner at the bottom of the details */}
          <div className="listing-disclaimer">
            <span className="material-symbols-outlined">gavel</span>
            <p>Privatverkauf: Ich schließe jegliche Sachmangelhaftung aus!</p>
          </div>
        </div>

        {/* Owner Actions - Simplified Logic */}
        {isAuthenticated && listing && (
          listing.is_owner || compareUserIds(userId, listing.user_id)
        ) && (
          <div className="owner-actions">
            <Link to={`/marketplace/edit/${id}`} className="edit-button">
              <span className="material-symbols-outlined">edit</span>
              Bearbeiten
            </Link>
            <button onClick={() => setShowConfirmDelete(true)} className="delete-button">
              <span className="material-symbols-outlined">delete</span>
              Löschen
            </button>
          </div>
        )}

        {/* Update the contact information section to properly display phone: */}
        <div className="seller-info">
          <h3>Verkäufer</h3>
          
          {/* Add seller trust badge here */}
          {listing.seller_trust_score !== undefined && (
            <SellerTrustBadge 
              trustScore={listing.seller_trust_score} 
              ratingCount={listing.seller_rating_count} 
            />
          )}
          
          <p>
            <span className="material-symbols-outlined">person</span>
            {listing.username}
          </p>
          
          {/* Phone display with improved handling */}
          {listing.phone_number && (
            <>
              {listing.displayed_phone ? (
                <p>
                  <span className="material-symbols-outlined">phone</span>
                  <a href={`tel:${listing.displayed_phone}`}>{listing.displayed_phone}</a>
                </p>
              ) : (
                listing.show_phone_to === 'R' && !isAuthenticated ? (
                  <p className="login-to-see">
                    <span className="material-symbols-outlined">phone</span>
                    <a href="/login">Anmelden, um Telefonnummer zu sehen</a>
                  </p>
                ) : listing.show_phone_to === 'N' ? null : (
                  <p className="phone-unavailable">
                    <span className="material-symbols-outlined">phone_disabled</span>
                    Keine Telefonnummer verfügbar
                  </p>
                )
              )}
            </>
          )}
          
          {/* Contact seller button - visible to all, but redirects to login if not authenticated */}
          {listing && !listing.is_owner && (
            <div className="seller-contact-section">
              <button 
                className="contact-seller-button"
                onClick={() => {
                  if (!isAuthenticated) {
                    navigate('/login');
                    return;
                  }
                  setShowContactForm(!showContactForm);
                }}
              >
                <span className="material-symbols-outlined">email</span>
                Verkäufer kontaktieren
              </button>
              
              {!isAuthenticated && (
                <p className="contact-info-note">
                  <span className="material-symbols-outlined">info</span>
                  Nur für registrierte Benutzer verfügbar
                </p>
              )}
            </div>
          )}
        </div>

        {/* Add this after the seller info section */}
        {isAuthenticated && !listing.is_owner && (
          <div className="seller-rating-section">
            <button 
              onClick={() => setShowRatingForm(!showRatingForm)} 
              className="toggle-rating-button"
            >
              <span className="material-symbols-outlined">
                {showRatingForm ? 'expand_less' : 'rate_review'}
              </span>
              {showRatingForm ? 'Bewertung ausblenden' : 'Verkäufer bewerten'}
            </button>
            
            {showRatingForm && (
              <SellerRating 
                listingId={id} 
                onRatingSubmitted={() => {
                  fetchRatings();
                  setTimeout(() => setShowRatingForm(false), 3000);
                }} 
              />
            )}
          </div>
        )}

        {/* Add a section to display ratings if there are any */}
        {ratings.length > 0 && (
          <div className="ratings-list">
            <h3>Bewertungen für diesen Verkäufer</h3>
            
            {ratings.map(rating => (
              <div key={rating.id} className="rating-item">
                <div className="rating-header">
                  <div className={`rating-type ${rating.rating.toLowerCase()}`}>
                    <span className="material-symbols-outlined">
                      {rating.rating === 'TOP' ? 'thumb_up' : 
                       rating.rating === 'OK' ? 'thumb_up_off' : 'thumb_down'}
                    </span>
                    {rating.rating}
                  </div>
                  <div className="rating-date">
                    {new Date(rating.rating_date).toLocaleDateString('de-DE')}
                  </div>
                </div>
                
                {rating.comment && (
                  <div className="rating-content">
                    "{rating.comment}"
                  </div>
                )}
                
                <div className="rating-user">
                  - {rating.rater_name}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Improved contact form */}
        {showContactForm && isAuthenticated && (
          <div className="contact-form-container">
            <div className="contact-form-header">
              <h3>Nachricht an Verkäufer</h3>
            </div>
            
            <form onSubmit={handleContactSubmit} className="contact-form">
              {formMessage && (
                <div className={`form-message ${formMessage.type}`}>
                  <span className="material-symbols-outlined">
                    {formMessage.type === 'success' ? 'check_circle' : 'error'}
                  </span>
                  <p>{formMessage.text}</p>
                </div>
              )}
              
              {/* Bezüglich-Container hierher verschieben, vor die Formularfelder */}
              <div className="listing-reference-container">
                <span className="reference-label">Bezüglich:</span>
                <span className="reference-content">{listing.title}</span>
                <span className="reference-id">ID: {id}</span>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact-name">Ihr Name:</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="contact-email">Ihre E-Mail:</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    value={contactForm.email || userEmail}
                    onChange={handleInputChange}
                    required
                    disabled={true}
                    className="readonly-field"
                  />
                  <small>Ihre registrierte E-Mail-Adresse wird verwendet</small>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="contact-message">Ihre Nachricht:</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleInputChange}
                  required
                  rows="5"
                  placeholder="Beschreiben Sie Ihr Interesse an diesem Artikel und stellen Sie Ihre Fragen..."
                  disabled={submitting}
                ></textarea>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowContactForm(false)} className="cancel-button">
                  <span className="material-symbols-outlined">close</span>
                  Abbrechen
                </button>
                
                <button type="submit" className="submit-button" disabled={submitting}>
                  {submitting ? (
                    <>
                      <div className="loading-spinner small"></div>
                      <span>Wird gesendet...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">send</span>
                      Nachricht senden
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      
      {showConfirmDelete && (
        <div className="confirm-delete-modal">
          <div className="modal-content">
            <h3>Angebot löschen?</h3>
            <p>Möchten Sie dieses Angebot wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="modal-buttons">
              <button onClick={() => setShowConfirmDelete(false)} className="cancel-button">
                Abbrechen
              </button>
              <button onClick={handleDeleteListing} className="confirm-button">
                Ja, löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Popup Modal */}
      {showImagePopup && listing.images && listing.images.length > 0 && (
        <div className="image-popup-overlay" onClick={() => setShowImagePopup(false)}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={listing.images[popupImageIndex]?.image_path} 
              alt={listing.title} 
              className="popup-image"
            />
            <div className="popup-navigation">
              <button 
                className="popup-nav-button"
                disabled={popupImageIndex === 0}
                onClick={() => setPopupImageIndex(prev => Math.max(0, prev - 1))}
              >
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
              <span className="popup-counter">{popupImageIndex + 1} / {listing.images.length}</span>
              <button 
                className="popup-nav-button"
                disabled={popupImageIndex === listing.images.length - 1}
                onClick={() => setPopupImageIndex(prev => Math.min(listing.images.length - 1, prev + 1))}
              >
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>
            <button className="popup-close-button" onClick={() => setShowImagePopup(false)}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceDetail;